import React from "react";
import { Link, createFileRoute, redirect } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { Card } from "~/components/ui/card";
import { getDBWithInit } from "~/lib/db";
import { FeatureCheck } from "~/components/ui/feature-check";
import { X } from "lucide-react";
import { cn } from "~/lib/cn";
import { createServerFn } from "@tanstack/react-start";
import { createNoIndexMeta } from "~/lib/shared/meta";

const ADMIN_KEY = "fbc84fb8-1e41-40fc-82c0-521bda7f041a";

// Admin access is checked in beforeLoad

// Load coaches data
export const getCoachesData = createServerFn({ method: "GET" }).handler(
  async () => {
    const db = await getDBWithInit();
    const coaches = await db.query.coaches.findMany({
      with: {
        profile: true,
        mainOffer: true,
        resources: true,
        testimonials: true,
        certifications: true,
        details: true,
        coachPoints: {
          where: (coachPoints, { eq }) => eq(coachPoints.type, "earned"),
        },
      },
      orderBy: (coaches, { asc }) => [asc(coaches.fullName)],
    });

    return coaches.map((coach) => {
      // Calculate profile completion status
      const isPersonalInfoComplete = Boolean(
        coach.firstName && coach.lastName && coach.email && coach.ghlLocationId,
      );

      const isProfileComplete = Boolean(
        coach.profile?.title &&
          coach.profile.description &&
          coach.profile.mainImage &&
          coach.profile.brandName,
      );

      const isOfferComplete = Boolean(
        coach.mainOffer?.headline && coach.mainOffer.description,
      );

      const isQualificationsComplete = Boolean(
        coach.profile?.coachingTitle && coach.profile.formOfConduct,
      );

      const isResourcesComplete = Boolean(0 < coach.resources.length);

      const completionStatus = {
        personalInfo: isPersonalInfoComplete,
        profile: isProfileComplete,
        offer: isOfferComplete,
        qualifications: isQualificationsComplete,
        resources: isResourcesComplete,
        isComplete:
          isPersonalInfoComplete &&
          isProfileComplete &&
          isOfferComplete &&
          isQualificationsComplete &&
          isResourcesComplete,
      };

      const profileQualityScore = [
        // Base points for having content
        coach.profile?.title ? 10 : 0,
        coach.profile?.description ? 10 : 0,
        coach.profile?.mainImage ? 10 : 0,
        coach.profile?.brandName ? 10 : 0,
        // Points for description length
        500 < (coach.profile?.description?.length ?? 0)
          ? 20
          : 200 < (coach.profile?.description?.length ?? 0)
            ? 10
            : 0,
        // Social proof points
        coach.testimonials.length * 5,
        coach.certifications.length * 3,
        // Offer points
        coach.mainOffer?.headline &&
        200 < (coach.mainOffer.description?.length ?? 0)
          ? 30
          : coach.mainOffer?.headline
            ? 15
            : 0,
      ].reduce((a, b) => a + b, 0);

      const totalDreamPoints = coach.coachPoints.reduce(
        (total, point) => total + point.points,
        0,
      );

      const hasCalendarIntegration = Boolean(coach.ghlCalendarId);
      const hasMatchingDetails = Boolean(
        coach.details?.mainCoachingCategory &&
          0 < (coach.details.typesOfCoaching ?? []).length &&
          0 < (coach.details.typesOfClients ?? []).length &&
          0 < (coach.details.typesOfProblems ?? []).length,
      );
      const hasJoinedCommunity = Boolean(coach.details?.joinedCommunityAt);

      return {
        ...coach,
        completionStatus,
        profileQualityScore,
        totalDreamPoints,
        hasCalendarIntegration,
        hasMatchingDetails,
        hasJoinedCommunity,
      };
    });
  },
);

const StatusIndicator = ({ value }: { value: boolean }) => (
  <div className="flex items-center justify-center">
    {value ? (
      <FeatureCheck className="h-6 w-6" />
    ) : (
      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-red-100">
        <X className="h-4 w-4 text-red-700" />
      </div>
    )}
  </div>
);

export const Route = createFileRoute("/admin/coaches/")({
  beforeLoad: ({ location }) => {
    const key = new URLSearchParams(location.search).get("key");
    if (key !== ADMIN_KEY) {
      throw redirect({ to: "/" });
    }
  },
  loader: async ({ context }) => {
    // Pre-load data into React Query
    await context.queryClient.ensureQueryData({
      queryKey: ["admin", "coaches"],
      queryFn: () => getCoachesData(),
    });
    return {};
  },
  head: () =>
    createNoIndexMeta(
      "Admin - Coach Directory – Dream Coach Match",
      "Admin dashboard for managing coaches",
    ),
  component: AdminCoachesPage,
});

function AdminCoachesPage() {
  const { data: coaches = [] } = useQuery({
    queryKey: ["admin", "coaches"],
    queryFn: () => getCoachesData(),
  });

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8 flex items-center justify-between">
        <h1 className="text-3xl font-bold">Coach Directory</h1>
        <div className="text-sm text-gray-500">
          {coaches.length} coaches total
        </div>
      </div>
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead className="text-center">Personal Info</TableHead>
              <TableHead className="text-center">Profile</TableHead>
              <TableHead className="text-center">Offer</TableHead>
              <TableHead className="text-center">Qualifications</TableHead>
              <TableHead className="text-center">Resources</TableHead>
              <TableHead className="text-center">Stripe</TableHead>
              <TableHead className="text-center">Calendar</TableHead>
              <TableHead className="text-center">Matching</TableHead>
              <TableHead className="text-center">Community</TableHead>
              <TableHead>Dream Points</TableHead>
              <TableHead>Plan</TableHead>
              <TableHead>Quality Score</TableHead>
              <TableHead>Last Updated</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {coaches.map((coach) => (
              <TableRow key={coach.id}>
                <TableCell>
                  <Link
                    to="/$username"
                    params={{ username: coach.username }}
                    target="_blank"
                    className="text-blue-600 hover:underline"
                  >
                    {coach.fullName}
                  </Link>
                </TableCell>
                <TableCell>{coach.email}</TableCell>
                <TableCell>
                  {coach.phone ? (
                    <a
                      href={`https://wa.me/${coach.phone.replace(/\D/g, "")}`}
                      target="_blank"
                      className="text-blue-600 hover:underline"
                    >
                      {coach.phone}
                    </a>
                  ) : null}
                </TableCell>
                <TableCell>
                  <StatusIndicator
                    value={coach.completionStatus.personalInfo}
                  />
                </TableCell>
                <TableCell>
                  <StatusIndicator value={coach.completionStatus.profile} />
                </TableCell>
                <TableCell>
                  <StatusIndicator value={coach.completionStatus.offer} />
                </TableCell>
                <TableCell>
                  <StatusIndicator
                    value={coach.completionStatus.qualifications}
                  />
                </TableCell>
                <TableCell>
                  <StatusIndicator value={coach.completionStatus.resources} />
                </TableCell>
                <TableCell>
                  <StatusIndicator value={true == coach.stripePayoutsEnabled} />
                </TableCell>
                <TableCell>
                  <StatusIndicator value={coach.hasCalendarIntegration} />
                </TableCell>
                <TableCell>
                  <StatusIndicator value={coach.hasMatchingDetails} />
                </TableCell>
                <TableCell>
                  <StatusIndicator value={coach.hasJoinedCommunity} />
                </TableCell>
                <TableCell>
                  <div
                    className={cn(
                      "font-medium",
                      0 < coach.totalDreamPoints
                        ? "text-green-600"
                        : "text-gray-500",
                    )}
                  >
                    {coach.totalDreamPoints}
                  </div>
                </TableCell>
                <TableCell>
                  <span
                    className={cn(
                      "rounded px-2 py-1 text-sm",
                      "ProPlus" === coach.membershipPlan &&
                        "bg-purple-100 text-purple-800",
                      "Pro" === coach.membershipPlan &&
                        "bg-blue-100 text-blue-800",
                      "Standard" === coach.membershipPlan &&
                        "bg-gray-100 text-gray-800",
                    )}
                  >
                    {coach.membershipPlan}
                  </span>
                </TableCell>
                <TableCell>
                  <span
                    className={cn(
                      "rounded px-2 py-1 text-sm",
                      80 <= coach.profileQualityScore &&
                        "bg-green-100 text-green-800",
                      50 <= coach.profileQualityScore &&
                        80 > coach.profileQualityScore &&
                        "bg-yellow-100 text-yellow-800",
                      50 > coach.profileQualityScore &&
                        "bg-red-100 text-red-800",
                    )}
                  >
                    {coach.profileQualityScore}
                  </span>
                </TableCell>
                <TableCell>
                  {coach.updatedAt &&
                    new Intl.DateTimeFormat("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    }).format(coach.updatedAt)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
